import { useRef } from 'react';
import axios, { AxiosRequestConfig, Canceler } from 'axios';
import { CANCEL_FLAG_MSG } from '@intelligence/utils/constant';

// eslint-disable-next-line prefer-destructuring
const CancelToken = axios.CancelToken;

/**
 * 增强请求方法，可自动取消重复请求，但是没考虑 请求参数是否一致，只针对调用次数做了 取消重复的操作，即多次调用，只会保留最后一次调用，前面的调用会cancel
 * 对传入的service方法的 参数定义有要求，1、第一个参数必须是接口的入参对象，2、第二个参数必须是 axios的其他配置对象
 * @param service 原请求方法，通常为service文件中的 请求接口的方法
 * @param options 自定义的配置对象，暂时只支持 配置主动取消的请求的异常回调
 * @returns 增加 自动取消重复请求特性的 请求方法，使用与原方法保持一致
 */
export default function useCancelTokenRequest<IReqParamsType, IResType>(
  // 原请求方法
  service: (
    // 原请求方法接口入参
    params: IReqParamsType,
    // axios配置对象
    config?: AxiosRequestConfig,
  ) => Promise<IResType>,
  // 自定义配置对象
  options?: {
    // 主动取消的请求的异常回调
    onCanceled?: (error: any) => void;
  },
) {
  const { onCanceled } = options || {};

  if (!(service instanceof Function)) {
    throw new Error('params service must be a function');
  }

  const canceler = useRef<Canceler | null>(null);

  const servicePlus = async (params: IReqParamsType, config?: AxiosRequestConfig): Promise<IResType> => {
    try {
      if (canceler.current) {
        canceler.current?.(CANCEL_FLAG_MSG);
      }
      const { token, cancel } = CancelToken.source();
      canceler.current = cancel;
      const res = await service(params, {
        ...config,
        cancelToken: token,
      });
      // 请求成功响应后，仅当仍是最新请求时才清空引用，防止竞态覆盖后续请求的 canceler
      if (canceler.current === cancel) {
        canceler.current = null;
      }
      return res;
    } catch (error: any) {
      // 被主动取消的请求 的 错误回调
      if (axios.isCancel(error) && error?.message === CANCEL_FLAG_MSG) {
        onCanceled?.(error);
      }
      return Promise.reject(error);
    }
  };

  return {
    service: servicePlus,
  };
}
